/**
 * Copilote AI Business - Complete Design System Theme
 * Unified visual identity across all modules
 */

export const THEME = {
  // ============================================
  // BRAND IDENTITY
  // ============================================
  brand: {
    name: "Copilote AI Business",
    shortName: "Copilote AI",
    slogan: "Votre copilote intelligent pour automatiser, apprendre et développer votre business",
    tagline: "AI-Powered Business Management",
    style: "Futuristic + Clean + Corporate Tech"
  },

  // ============================================
  // COLOR PALETTE - LIGHT MODE
  // ============================================
  colors: {
    light: {
      // Primary Colors
      primary: {
        DEFAULT: "#007BFF",
        50: "#E6F2FF",
        100: "#CCE5FF",
        200: "#99CCFF",
        300: "#66B2FF",
        400: "#3399FF",
        500: "#007BFF",
        600: "#0062CC",
        700: "#004A99",
        800: "#003166",
        900: "#001933",
      },
      
      // Secondary Colors
      secondary: {
        DEFAULT: "#6366F1",
        light: "#818CF8",
        dark: "#4338CA",
      },
      
      // Accent Colors
      accent: {
        success: "#22C55E",
        warning: "#F59E0B",
        error: "#EF4444",
        info: "#3B82F6",
      },
      
      // Neutral Colors
      neutral: {
        50: "#F9FAFB",
        100: "#F3F4F6",
        200: "#E5E7EB",
        300: "#D1D5DB",
        400: "#9CA3AF",
        500: "#6B7280",
        600: "#4B5563",
        700: "#374151",
        800: "#1F2937",
        900: "#111827",
      },
      
      // Semantic Colors
      background: {
        primary: "#FFFFFF",
        secondary: "#F9FAFB",
        tertiary: "#F3F4F6",
        elevated: "#FFFFFF",
      },
      
      text: {
        primary: "#111827",
        secondary: "#374151",
        tertiary: "#6B7280",
        inverse: "#FFFFFF",
        link: "#007BFF",
      },
      
      border: {
        DEFAULT: "#E5E7EB",
        light: "#F3F4F6",
        dark: "#D1D5DB",
      },
      
      // Gradient Colors
      gradient: {
        primary: "linear-gradient(135deg, #007BFF 0%, #66B2FF 100%)",
        secondary: "linear-gradient(135deg, #6366F1 0%, #818CF8 100%)",
        success: "linear-gradient(135deg, #22C55E 0%, #86EFAC 100%)",
        warm: "linear-gradient(135deg, #F59E0B 0%, #FCD34D 100%)",
      }
    },
    
    // ============================================
    // COLOR PALETTE - DARK MODE
    // ============================================
    dark: {
      // Primary Colors (slightly adjusted for dark)
      primary: {
        DEFAULT: "#3399FF",
        50: "#001933",
        100: "#003166",
        200: "#004A99",
        300: "#0062CC",
        400: "#007BFF",
        500: "#3399FF",
        600: "#66B2FF",
        700: "#99CCFF",
        800: "#CCE5FF",
        900: "#E6F2FF",
      },
      
      secondary: {
        DEFAULT: "#818CF8",
        light: "#A5B4FC",
        dark: "#6366F1",
      },
      
      accent: {
        success: "#4ADE80",
        warning: "#FBBF24",
        error: "#F87171",
        info: "#60A5FA",
      },
      
      neutral: {
        50: "#111827",
        100: "#1F2937",
        200: "#374151",
        300: "#4B5563",
        400: "#6B7280",
        500: "#9CA3AF",
        600: "#D1D5DB",
        700: "#E5E7EB",
        800: "#F3F4F6",
        900: "#F9FAFB",
      },
      
      background: {
        primary: "#0F172A",
        secondary: "#1E293B",
        tertiary: "#334155",
        elevated: "#1E293B",
      },
      
      text: {
        primary: "#F9FAFB",
        secondary: "#E5E7EB",
        tertiary: "#9CA3AF",
        inverse: "#111827",
        link: "#66B2FF",
      },
      
      border: {
        DEFAULT: "#334155",
        light: "#1E293B",
        dark: "#475569",
      },
      
      gradient: {
        primary: "linear-gradient(135deg, #3399FF 0%, #66B2FF 100%)",
        secondary: "linear-gradient(135deg, #818CF8 0%, #A5B4FC 100%)",
        success: "linear-gradient(135deg, #4ADE80 0%, #86EFAC 100%)",
        warm: "linear-gradient(135deg, #FBBF24 0%, #FCD34D 100%)",
      }
    }
  },

  // ============================================
  // TYPOGRAPHY
  // ============================================
  typography: {
    // Font Families
    fonts: {
      primary: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
      heading: "'Poppins', 'Inter', sans-serif",
      mono: "'Fira Code', 'Monaco', 'Courier New', monospace",
    },
    
    // Font Sizes (responsive scale)
    size: {
      xs: "0.75rem",      // 12px
      sm: "0.875rem",     // 14px
      base: "1rem",       // 16px
      lg: "1.125rem",     // 18px
      xl: "1.25rem",      // 20px
      "2xl": "1.5rem",    // 24px
      "3xl": "1.875rem",  // 30px
      "4xl": "2.25rem",   // 36px
      "5xl": "3rem",      // 48px
      "6xl": "3.75rem",   // 60px
      "7xl": "4.5rem",    // 72px
    },
    
    // Font Weights
    weight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      extrabold: 800,
    },
    
    // Line Heights
    lineHeight: {
      none: 1,
      tight: 1.25,
      snug: 1.375,
      normal: 1.5,
      relaxed: 1.625,
      loose: 2,
    },
    
    // Letter Spacing
    letterSpacing: {
      tighter: "-0.05em",
      tight: "-0.025em",
      normal: "0",
      wide: "0.025em",
      wider: "0.05em",
      widest: "0.1em",
    }
  },

  // ============================================
  // SPACING SYSTEM (8px base)
  // ============================================
  spacing: {
    0: "0",
    px: "1px",
    0.5: "0.125rem",  // 2px
    1: "0.25rem",     // 4px
    1.5: "0.375rem",  // 6px
    2: "0.5rem",      // 8px
    2.5: "0.625rem",  // 10px
    3: "0.75rem",     // 12px
    3.5: "0.875rem",  // 14px
    4: "1rem",        // 16px
    5: "1.25rem",     // 20px
    6: "1.5rem",      // 24px
    7: "1.75rem",     // 28px
    8: "2rem",        // 32px
    9: "2.25rem",     // 36px
    10: "2.5rem",     // 40px
    11: "2.75rem",    // 44px
    12: "3rem",       // 48px
    14: "3.5rem",     // 56px
    16: "4rem",       // 64px
    20: "5rem",       // 80px
    24: "6rem",       // 96px
    28: "7rem",       // 112px
    32: "8rem",       // 128px
    36: "9rem",       // 144px
    40: "10rem",      // 160px
    44: "11rem",      // 176px
    48: "12rem",      // 192px
    52: "13rem",      // 208px
    56: "14rem",      // 224px
    60: "15rem",      // 240px
    64: "16rem",      // 256px
    72: "18rem",      // 288px
    80: "20rem",      // 320px
    96: "24rem",      // 384px
  },

  // ============================================
  // BORDER RADIUS
  // ============================================
  borderRadius: {
    none: "0",
    sm: "0.25rem",    // 4px
    DEFAULT: "0.375rem",  // 6px
    md: "0.5rem",     // 8px
    lg: "0.75rem",    // 12px
    xl: "1rem",       // 16px
    "2xl": "1.5rem",  // 24px
    "3xl": "2rem",    // 32px
    full: "9999px",
  },

  // ============================================
  // SHADOWS & ELEVATION
  // ============================================
  shadows: {
    sm: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    DEFAULT: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    md: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    lg: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
    xl: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    "2xl": "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    inner: "inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)",
    none: "none",
    
    // Custom colored shadows
    glow: {
      primary: "0 0 20px rgba(0, 123, 255, 0.15)",
      primaryStrong: "0 0 30px rgba(0, 123, 255, 0.3)",
      success: "0 0 20px rgba(34, 197, 94, 0.15)",
      error: "0 0 20px rgba(239, 68, 68, 0.15)",
    }
  },

  // ============================================
  // ANIMATIONS & TRANSITIONS
  // ============================================
  animation: {
    duration: {
      fast: "150ms",
      normal: "300ms",
      slow: "500ms",
      slower: "750ms",
      slowest: "1000ms",
    },
    
    easing: {
      linear: "linear",
      in: "cubic-bezier(0.4, 0, 1, 1)",
      out: "cubic-bezier(0, 0, 0.2, 1)",
      inOut: "cubic-bezier(0.4, 0, 0.2, 1)",
      spring: "cubic-bezier(0.68, -0.55, 0.265, 1.55)",
    },
    
    keyframes: {
      fadeIn: {
        from: { opacity: 0 },
        to: { opacity: 1 },
      },
      fadeInUp: {
        from: { opacity: 0, transform: "translateY(20px)" },
        to: { opacity: 1, transform: "translateY(0)" },
      },
      scaleIn: {
        from: { transform: "scale(0.95)", opacity: 0 },
        to: { transform: "scale(1)", opacity: 1 },
      },
      slideInRight: {
        from: { transform: "translateX(100%)" },
        to: { transform: "translateX(0)" },
      },
      pulse: {
        "0%, 100%": { opacity: 1 },
        "50%": { opacity: 0.5 },
      },
      glow: {
        "0%, 100%": { boxShadow: "0 0 20px rgba(0, 123, 255, 0.15)" },
        "50%": { boxShadow: "0 0 30px rgba(0, 123, 255, 0.3)" },
      }
    }
  },

  // ============================================
  // BREAKPOINTS (Responsive)
  // ============================================
  breakpoints: {
    xs: "480px",
    sm: "640px",
    md: "768px",
    lg: "1024px",
    xl: "1280px",
    "2xl": "1536px",
  },

  // ============================================
  // Z-INDEX LAYERS
  // ============================================
  zIndex: {
    base: 0,
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
    notification: 1080,
  },

  // ============================================
  // COMPONENT SPECIFIC STYLES
  // ============================================
  components: {
    button: {
      padding: {
        sm: "0.5rem 1rem",
        md: "0.75rem 1.5rem",
        lg: "1rem 2rem",
      },
      fontSize: {
        sm: "0.875rem",
        md: "1rem",
        lg: "1.125rem",
      },
      borderRadius: "0.375rem",
      transition: "all 300ms cubic-bezier(0.4, 0, 0.2, 1)",
    },
    
    card: {
      padding: "1.5rem",
      borderRadius: "0.75rem",
      shadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
      hoverShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1)",
      transition: "all 300ms cubic-bezier(0.4, 0, 0.2, 1)",
    },
    
    input: {
      padding: "0.75rem 1rem",
      borderRadius: "0.375rem",
      fontSize: "1rem",
      borderWidth: "1px",
      focusRing: "0 0 0 3px rgba(0, 123, 255, 0.1)",
    },
    
    modal: {
      backdropBlur: "blur(12px)",
      maxWidth: "640px",
      padding: "2rem",
      borderRadius: "1rem",
    },
    
    navbar: {
      height: "64px",
      padding: "1rem 1.5rem",
      backdropBlur: "blur(12px)",
    },
    
    sidebar: {
      width: "256px",
      padding: "1.5rem",
      activeIndicator: "4px",
    }
  },

  // ============================================
  // LAYOUT CONSTRAINTS
  // ============================================
  layout: {
    maxWidth: {
      sm: "640px",
      md: "768px",
      lg: "1024px",
      xl: "1280px",
      "2xl": "1536px",
      full: "100%",
    },
    
    container: {
      padding: {
        mobile: "1rem",
        tablet: "1.5rem",
        desktop: "2rem",
      }
    }
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

export const getColor = (path, isDark = false) => {
  const mode = isDark ? 'dark' : 'light';
  const parts = path.split('.');
  let color = THEME.colors[mode];
  
  for (const part of parts) {
    color = color[part];
    if (!color) return THEME.colors[mode].primary.DEFAULT;
  }
  
  return color;
};

export const getShadow = (type = 'DEFAULT', glow = false) => {
  if (glow && THEME.shadows.glow[type]) {
    return THEME.shadows.glow[type];
  }
  return THEME.shadows[type] || THEME.shadows.DEFAULT;
};

export const getSpacing = (value) => {
  return THEME.spacing[value] || value;
};

export default THEME;
